"use client";

import Link from "next/link";
import { motion } from "framer-motion";

interface HeroSectionProps {
  showButton: boolean;
  buttonText: string;
  buttonHref: string;
}

export function HeroSection({
  showButton,
  buttonText,
  buttonHref,
}: HeroSectionProps) {
  return (
    <section className="relative min-h-[90vh] flex flex-col justify-center items-center px-6 md:px-12 pt-20">
      <div className="max-w-4xl w-full text-center space-y-12">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1.1] md:leading-[1.1] tracking-tight text-ink"
        >
          A blogging platform <br />
          that lets you <span className="italic font-light">leave.</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="flex flex-col items-center gap-8"
        >
          <div className="h-12 w-[1px] bg-stone/30 mb-2"></div>

          <p className="font-sans text-stone text-lg md:text-xl max-w-lg leading-relaxed font-light">
            Weaven is a blogging platform built on{" "}
            <span className="text-ink font-normal">Sanity</span>, designed for
            people who want to <span className="text-ink font-normal">own</span>{" "}
            their writing — not hand it over.
          </p>

          {showButton && (
            <div className="pt-4">
              <Link
                href={buttonHref}
                className="px-10 py-4 bg-ink text-paper font-sans font-medium text-sm tracking-widest uppercase rounded-sm hover:bg-ink/90 transition inline-block"
              >
                {buttonText}
              </Link>
            </div>
          )}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute hidden md:block bottom-10 left-1/2 -translate-x-1/2 text-xs font-sans text-stone/50 tracking-widest uppercase"
      >
        Scroll to understand
      </motion.div>
    </section>
  );
}
